import { useRef, useEffect, useContext, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { CSSTransition } from 'react-transition-group';

import Button from 'components/common/Button';
import BodyModal from 'components/common/modals/BodyModal';
import { COLOR_SCHEME_CREAM } from 'components/common/modals/Modal';
import StrikethroughSkeleton from 'components/common/StrikethroughSkeleton';
import Text from 'components/common/Text';
import { VoucherContext } from 'components/signUpFunnel/Contexts';
import { displayFunnelServingsText } from 'helpers/servings';
import { TEMPO, STANDARD_MEAL_SERVING_SIZE } from 'lib/constants';
import RequestService from 'services/RequestService';

const PickMoreMealsModal = ({
  userServings,
  totalServings,
  isOpen,
  onDismiss,
  type,
  isSubmitting,
  onContinue,
}) => {
  const transitionRef = useRef();
  const priceChange = type === 'priceChange';
  const { voucher } = useContext(VoucherContext);
  const [priceChangeSummary, setPriceChangeSummary] = useState(null);

  useEffect(() => {
    const fetchProjectedCosts = async () => {
      if (priceChange) {
        const response = await RequestService.getProjectedCosts({
          params: {
            weeklyBasket: {
              numberOfMeals: totalServings / STANDARD_MEAL_SERVING_SIZE,
              mealPlanSlug: TEMPO,
              servings: userServings,
              voucherCode: voucher?.code,
            },
          },
        });
        if (response?.ok) {
          setPriceChangeSummary(response.weeklyBasket);
        }
      }
    };

    fetchProjectedCosts();
  }, [userServings, totalServings, priceChange, voucher]);

  const getModalContent = () => {
    if (type === 'lowMeals') {
      return {
        header: "You're so close!",
        body: 'You need a few more meals to complete your order.',
      };
    }

    if (priceChange) {
      const header = 'Updated box details';

      if (!totalServings || !priceChangeSummary) {
        return {
          header,
          body: <Skeleton count={1} height={10} />,
        };
      }

      return {
        header,
        body: (
          <>
            Your new weekly meal plan will be{' '}
            {displayFunnelServingsText({ servings: totalServings, increment: 1 })} meals at
            {priceChangeSummary.promoApplied &&
            priceChangeSummary.subtotalFormatted !== priceChangeSummary.promo.subtotalFormatted ? (
              <>
                <StrikethroughSkeleton
                  strikethroughText={priceChangeSummary.subtotalFormatted}
                  textDisplayed={priceChangeSummary.promo.subtotalFormatted}
                  ariaLabel="subtotal"
                  className="!px-1"
                />
              </>
            ) : (
              <Text htmlElement="span" brandStylesOverrides="textMd">
                {' '}
                {priceChangeSummary.subtotalFormatted}
              </Text>
            )}
            .
          </>
        ),
      };
    }

    return { header: '', body: '' };
  };

  const { header, body } = getModalContent();

  return (
    <CSSTransition
      in={isOpen}
      timeout={100}
      classNames="fadeInOut"
      unmountOnExit
      nodeRef={transitionRef}
    >
      <BodyModal
        onDismiss={onDismiss}
        colorScheme={COLOR_SCHEME_CREAM}
        transitionRef={transitionRef}
        contentClassname="!bg-cream-100"
        showIcon={false}
        bodyPadding="px-6 pb-8"
        topBarStyle="!px-6 !pt-4 !pb-0"
        isMinHeight={true}
      >
        <Text brandStylesOverrides="h6 text-tempo-black-100" className="mb-2">
          {header}
        </Text>
        <Text brandStylesOverrides="textMd text-tempo-black-100 mb-6">{body}</Text>
        <div className="flex w-full flex-col items-center bp1:flex-row bp1:justify-end">
          <Button
            className="btn w-full bp1:w-auto"
            variation={priceChange ? 'Secondary' : 'Primary'}
            onClick={onDismiss}
          >
            Pick More Meals
          </Button>

          {priceChange && (
            <Button
              type="submit"
              onClick={onContinue}
              className="mt-4 w-full bp1:ml-4 bp1:mt-0 bp1:w-auto"
              isSubmitting={isSubmitting}
            >
              Continue to Billing
            </Button>
          )}
        </div>
      </BodyModal>
    </CSSTransition>
  );
};

PickMoreMealsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onDismiss: PropTypes.func.isRequired,
  type: PropTypes.string,
  orderSummary: PropTypes.shape({
    subtotalFormatted: PropTypes.string,
    promo: PropTypes.shape({
      subtotalFormatted: PropTypes.string,
    }),
    promoApplied: PropTypes.bool,
  }),
  totalServings: PropTypes.number,
  isSubmitting: PropTypes.bool,
  onContinue: PropTypes.func,
  userServings: PropTypes.number,
};

export default PickMoreMealsModal;
